import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { ROUTES } from '~shared/config/routes';
import { nonNullableValue } from '~shared/lib/non-nullable-value/non-nullable-value';
import { DashboardTable } from '~shared/ui/dashboard-table';
import type {
  DashboardTableRef,
  RowActionOptions,
} from '~shared/ui/dashboard-table';
import { Loader } from '~shared/ui/loader';

import { Int24Plus } from '@platform-ui/iconsPack';
import Popup from '@platform-ui/popup';

import Button from '@breeze-platform-ui/button';

import { OrderDetails } from './order-details/order-details';

import tableImg from '../images/table.webp';
import { columns } from '../lib/columns';
import {
  colActionOptions,
  colSizingOptions,
  defaultPagination,
  defaultState,
} from '../lib/table-options';
import type { OrderTableData } from '../model/orders-table-data';
import { useOrdersTableDataQuery } from '../model/orders-table-queries';

export const OrdersTable = () => {
  const tableRef = useRef<DashboardTableRef>(null);
  const [pagination, setPagination] = useState<{
    page: number;
    perPage: number;
  }>(defaultPagination);
  const { data, isLoading } = useOrdersTableDataQuery({
    ...pagination,
  });
  const [orderDetailsOpened, setOrderDetailsOpened] = useState(false);
  const lastSelectedOrderRef = useRef<OrderTableData | null>(null);
  const navigate = useNavigate();

  const rowOptions: RowActionOptions<OrderTableData> = useMemo(
    () => ({
      isHoverable: true,
      onClick(item) {
        lastSelectedOrderRef.current = item;
        setOrderDetailsOpened(true);
      },
    }),
    []
  );

  const handlePopupClosed = () => {
    setOrderDetailsOpened(false);
  };

  const hasOrders = data?.orders && data.orders.length > 0;

  return (
    <div className="flex min-h-[226px] flex-col gap-8">
      <div className="relative flex grow flex-col">
        {isLoading && <Loader />}
        {hasOrders && (
          <DashboardTable
            screen="payments"
            isPaginationEnabled
            data={data?.orders}
            totalAmount={data?.total}
            columns={columns}
            defaultState={defaultState}
            colSizingOptions={colSizingOptions}
            colActionOptions={colActionOptions}
            innerRef={tableRef}
            onStateChange={(state) =>
              setPagination({
                page: state.pagination?.page ?? defaultPagination.page,
                perPage:
                  state.pagination?.rowsPerPage ?? defaultPagination.perPage,
              })
            }
            rowActionOptions={rowOptions}
          />
        )}
        {!hasOrders && !isLoading && (
          <div
            className={`
              flex h-full w-full flex-col items-center justify-center gap-8
              py-10
            `}
          >
            <img src={tableImg} width="116" height="116" alt="" />
            <h3 className="my-0 font-heading text-2xl font-bold">
              No orders yet
            </h3>
            <Button
              size="m"
              icon={<Int24Plus />}
              onClick={() => navigate(ROUTES.orders.create)}
            >
              Add order
            </Button>
          </div>
        )}
      </div>
      <Popup
        closeable
        size="s"
        opened={orderDetailsOpened}
        onClose={handlePopupClosed}
      >
        <OrderDetails order={nonNullableValue(lastSelectedOrderRef.current)} />
      </Popup>
    </div>
  );
};
